import { useCallback, useEffect, useMemo } from 'react';

import {
  AllowListPhoneNumberFragment,
  CreateAllowListPhoneNumberMutation,
  GetAllowListPhoneNumbersQuery,
  UpdateAllowListPhoneNumberMutation,
} from '@occc/graphql';
import { snackbar } from '@occc-app/design-system';
import { formatPhoneNumberNationalForUS } from '@occc/utils-phone-number';

import { UpsertAllowListPhoneNumberFormValues } from './create-comment-modal.types';
import { convertAllowListPhoneNumberExistingEntityToFormValues } from './convert-allow-list-phone-number-existing-entity-to-form-values';
import {
  convertAllowListPhoneNumberFormValuesToCreateInput,
  convertAllowListPhoneNumberFormValuesToUpdateInput,
} from './convert-allow-list-phone-number-form-values-to-inputs';

import {
  useCreateAllowListPhoneNumberMutation,
  useUpdateAllowListPhoneNumberMutation,
} from '../hooks/allow-list.query.hook';

type UseUpsertPhoneNumberModalContainerParams = {
  isCreateMode: boolean;
  onClose(): void;
  existingEntity?: AllowListPhoneNumberFragment;
};

export const useUpsertPhoneNumberModalContainer = ({
  existingEntity,
  isCreateMode,
  onClose,
}: UseUpsertPhoneNumberModalContainerParams) => {
  useEffect(() => {
    if (!isCreateMode && !existingEntity) {
      onClose();
    }
  }, [existingEntity, isCreateMode, onClose]);

  const modalTitle = isCreateMode ? 'Add Number to Allow List' : 'Edit Entry';
  const submitButtonTitle = isCreateMode ? 'Add Number' : 'Save changes';

  const initialValues = useMemo<UpsertAllowListPhoneNumberFormValues>(
    (): UpsertAllowListPhoneNumberFormValues =>
      convertAllowListPhoneNumberExistingEntityToFormValues({ existingEntity, isCreateMode }),
    [existingEntity, isCreateMode],
  );

  const onCreateCompleted = useCallback(
    ({ createAllowListPhoneNumber: { phoneNumber } }: CreateAllowListPhoneNumberMutation) => {
      snackbar.success(
        `${formatPhoneNumberNationalForUS(phoneNumber)} has been successfully created`,
      );
      onClose();
    },
    [onClose],
  );
  const [createPhoneNumber] = useCreateAllowListPhoneNumberMutation({
    refetchQueries: ['getAllowListPhoneNumbers' satisfies keyof GetAllowListPhoneNumbersQuery],
    onCompleted: onCreateCompleted,
  });

  const onUpdateCompleted = useCallback(
    ({ updateAllowListPhoneNumber: { phoneNumber } }: UpdateAllowListPhoneNumberMutation) => {
      snackbar.success(
        `${formatPhoneNumberNationalForUS(phoneNumber)} has been successfully updated`,
      );
      onClose();
    },
    [onClose],
  );
  const [updatePhoneNumber] = useUpdateAllowListPhoneNumberMutation({
    refetchQueries: ['getAllowListPhoneNumbers' satisfies keyof GetAllowListPhoneNumbersQuery],
    onCompleted: onUpdateCompleted,
  });

  const handleSubmit = useCallback(
    (formValues: UpsertAllowListPhoneNumberFormValues) => {
      if (isCreateMode) {
        return createPhoneNumber({
          variables: { input: convertAllowListPhoneNumberFormValuesToCreateInput(formValues) },
        });
      }

      if (existingEntity?.uuid) {
        return updatePhoneNumber({
          variables: {
            input: convertAllowListPhoneNumberFormValuesToUpdateInput(
              formValues,
              existingEntity.uuid,
            ),
          },
        });
      }

      return;
    },
    [createPhoneNumber, existingEntity?.uuid, isCreateMode, updatePhoneNumber],
  );

  return useMemo(
    () => ({
      modalTitle,
      handleSubmit,
      initialValues,
      submitButtonTitle,
    }),
    [handleSubmit, initialValues, modalTitle, submitButtonTitle],
  );
};
